import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { L as Link, useLocaleNavigate } from "../lib/i18n";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle, Crown } from "lucide-react";
import { trackEvent } from "../lib/analytics";

export default function CheckoutSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useLocaleNavigate();
  const [state, setState] = useState({ kind: "checking" });

  useEffect(() => {
    const sessionId = params.get("session_id");
    const flowFromUrl = params.get("flow"); // boost | premium | dealer_sub | (default subscription)
    if (!sessionId) { navigate("/pricing"); return; }
    let attempts = 0;
    let cancelled = false;
    const MAX_ATTEMPTS = 10;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await api.checkoutStatus(sessionId);
        if (res.payment_status === "paid") {
          const flow = res.flow || flowFromUrl || "subscription";
          setState({ kind: "paid", res, flow });
          // Phase 64 — terminal conversion event
          trackEvent("subscription_started", {
            flow,
            session_id: sessionId,
            package: res.package,
            trial: !!res.trial,
            amount_chf: res.amount_chf,
          });
          if (res.trial) {
            trackEvent("trial_start", { flow, package: res.package, session_id: sessionId });
          }
          return;
        }
        if (res.status === "expired") {
          setState({ kind: "failed", flow: res.flow || flowFromUrl });
          return;
        }
      } catch (err) {
        // 404 on /checkout/status means the session is unknown to us — stop polling
        // immediately instead of retrying 10x and spamming the network tab.
        if (err?.response?.status === 404) {
          setState({ kind: "failed", flow: flowFromUrl });
          return;
        }
        // keep polling for transient errors
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) { setState({ kind: "failed", flow: flowFromUrl }); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [params, navigate]);

  const isBoost = state.flow === "boost" || params.get("flow") === "boost";
  const isPremium = state.flow === "premium" || params.get("flow") === "premium";
  const isDealerSub = state.flow === "dealer_sub" || params.get("flow") === "dealer_sub";
  const listingId = state.res?.listing_id;

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md items-center px-4" data-testid="checkout-success-page">
      <div className="w-full rounded-lg border border-zinc-800 bg-zinc-950 p-10 text-center">
        {state.kind === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-red-500" data-testid="checkout-loading" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white">{t.pricing.checking}</h1>
            <p className="mt-3 text-sm text-zinc-400">
              {isBoost ? "Boost-Zahlung wird verifiziert…"
                : isPremium ? "Premium-Zahlung wird verifiziert…"
                : isDealerSub ? "Dealer-Abo wird aktiviert…"
                : "Stripe-Zahlung wird verifiziert."}
            </p>
          </>
        )}
        {state.kind === "paid" && isBoost && (
          <>
            <div className="relative mx-auto grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-red-600 to-orange-500 shadow-lg shadow-red-600/40" data-testid="boost-success">
              <Crown className="h-7 w-7 text-white" />
            </div>
            <h1 className="mt-6 font-display text-2xl font-bold text-white">{t.boost?.success || "Inserat hervorgehoben"} 🎉</h1>
            <p className="mt-3 text-sm text-zinc-400">
              {t.boost?.successDescription || "Dein Inserat erscheint jetzt zuoberst in den Suchresultaten."}
            </p>
            {listingId && (
              <Button asChild className="mt-8 w-full bg-gradient-to-r from-red-600 to-orange-500 text-white hover:from-red-500 hover:to-orange-400" data-testid="goto-listing">
                <Link to={`/listings/${listingId}`}>{t.boost?.viewListing || "Inserat ansehen"}</Link>
              </Button>
            )}
            <Button asChild variant="outline" className="mt-3 w-full border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
              <Link to="/dashboard">Mein Dashboard</Link>
            </Button>
          </>
        )}
        {state.kind === "paid" && isPremium && (
          <>
            <div className="relative mx-auto grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 shadow-lg shadow-amber-500/40" data-testid="premium-success">
              <Crown className="h-7 w-7 text-black" />
            </div>
            <h1 className="mt-6 font-display text-2xl font-bold text-white">Premium aktiviert 👑</h1>
            <p className="mt-3 text-sm text-zinc-400">
              Dein Inserat erscheint 30 Tage lang mit Premium-Badge und Priority Placement.
            </p>
            {listingId && (
              <Button asChild className="mt-8 w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-black hover:from-amber-400 hover:to-yellow-400" data-testid="goto-listing">
                <Link to={`/listings/${listingId}`}>Inserat ansehen</Link>
              </Button>
            )}
            <Button asChild variant="outline" className="mt-3 w-full border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
              <Link to="/dashboard">Mein Dashboard</Link>
            </Button>
          </>
        )}
        {state.kind === "paid" && isDealerSub && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-amber-400" data-testid="dealer-sub-success" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white">Dealer-Abo aktiv 🏢</h1>
            <p className="mt-3 text-sm text-zinc-400">
              Dein monatliches Dealer-Abo ist aktiv. Du kannst jetzt mehr Inserate verwalten und Analytics einsehen.
            </p>
            <Button asChild className="mt-8 w-full bg-amber-500 text-black hover:bg-amber-400" data-testid="goto-dealer-dashboard">
              <Link to="/dashboard">Mein Dashboard</Link>
            </Button>
            <Button asChild variant="outline" className="mt-3 w-full border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
              <Link to="/sell">Erstes Inserat erstellen</Link>
            </Button>
          </>
        )}
        {state.kind === "paid" && !isBoost && !isPremium && !isDealerSub && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-red-500" data-testid="subscription-success" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white">{t.pricing.success}</h1>
            <Button asChild className="mt-8 w-full bg-red-600 hover:bg-red-500" data-testid="goto-sell">
              <Link to="/sell">{t.pricing.goToSell}</Link>
            </Button>
            <Button asChild variant="outline" className="mt-3 w-full border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
              <Link to="/dashboard">Mein Dashboard</Link>
            </Button>
          </>
        )}
        {state.kind === "failed" && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-zinc-500" data-testid="checkout-failed" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white">{t.pricing.failed}</h1>
            <p className="mt-3 text-sm text-zinc-400">
              {isBoost ? "Boost-Zahlung konnte nicht bestätigt werden."
                : isPremium ? "Premium-Zahlung konnte nicht bestätigt werden."
                : isDealerSub ? "Dealer-Abo konnte nicht aktiviert werden."
                : "Zahlung konnte nicht bestätigt werden."}
            </p>
            <Button asChild className="mt-8 w-full bg-red-600 hover:bg-red-500">
              <Link to={isBoost || isPremium ? "/dashboard" : "/pricing"}>{(isBoost || isPremium) ? "Zurück zum Dashboard" : t.pricing.choosePlan}</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
